import React, { useState } from 'react'

import styled from 'styled-components'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import RdnaDrawer from '@ringdna/common/src/components/RdnaDrawer'
import RdnaTextInput from '@ringdna/common/src/components/RdnaFormElements/RdnaTextInput'
import RdnaSection from '@ringdna/common/src/components/RdnaFormElements/RdnaFormLayout/RdnaSection'
import RdnaForm from '@ringdna/common/src/components/RdnaFormElements'
import RdnaFormLayout from '@ringdna/common/src/components/RdnaFormElements/RdnaFormLayout'
import { getRules } from '@ringdna/common/src/components/RdnaFormElements/_helpers'
import { InputTypes } from '@ringdna/common/src/constants/input-types'
import { useCreateUser, Location } from '../../../store/api/users-api'
import RdnaAutocomplete2, {
  makeAutoCompleteProps,
  makeTextInputProps
} from '@ringdna/common/src/components/RdnaFormElements/RdnaAutocomplete2'
import { RdnaAccount } from '@ringdna/common/src/types/accounts'
import RdnaCheckbox from '@ringdna/common/src/components/RdnaFormElements/RdnaCheckbox'
import { serializeJsonIntoPlay } from '@ringdna/common/src/utils/serverUtils'
import { License, Option } from './utils'
import { getErrorMessage } from '@ringdna/common/src/utils/errorUtils'
import { flags, isFlagged } from '@ringdna/common/src/utils/feature-flag/featureFlag'

type Props = {
  selectedAccount: RdnaAccount
  onSave: (reload: boolean) => void
  isOpen: boolean
  onClose: () => void
  remainingLicenses: { [key: string]: number }
  locations: Location[]
}

type UserForm = {
  accountId: number
  firstName: string
  lastName: string
  email: string
  salesforceId: string
  location: Option
  'licenses.admin': boolean
  'licenses.intelligentDialer': boolean
  'licenses.calendar': boolean
  'licenses.conversationAI': boolean
  'licenses.guidedSelling': boolean
  'licenses.moments': boolean
  'licenses.revenueAI': boolean
}

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  salesforceId: '',
  'licenses.admin': false,
  'licenses.intelligentDialer': false,
  'licenses.calendar': false,
  'licenses.conversationAI': false,
  'licenses.guidedSelling': false,
  'licenses.moments': false,
  'licenses.revenueAI': false
}

const QUICK_HEAD = 'Create New User'
const ERROR_LICENSE = 'You do not have the available licenses.'
const MAX_STRING = 100

export default function QuickAdd({ selectedAccount, onSave, isOpen, onClose, remainingLicenses, locations }: Props) {
  const locationRequired = selectedAccount.settings.locationRequired
  const createUser = useCreateUser()
  const [isSaving, setSaving] = useState<boolean>(false)

  const onSaveUser = (formValues: UserForm) => {
    setSaving(true)
    createUser({
      body: serializeJsonIntoPlay({
        ...formValues,
        accountId: selectedAccount.id,
        locationId: formValues.location?.value || null
      })
    })
      .then(response => response.success && onSave(true))
      .catch(error => toastr.error(getErrorMessage(error)))
      .finally(() => setSaving(false))
  }

  return (
    <Drawer open={isOpen} onClose={onClose} heading={QUICK_HEAD}>
      <RdnaSection>
        <RdnaForm<UserForm> defaultValues={{ ...defaultValues }}>
          {({ handleSubmit }, { RdnaFormController, getMuiProps }) => (
            <RdnaFormLayout container>
              <Column>
                <RdnaFormLayout>
                  <RdnaFormController
                    name="firstName"
                    rules={getRules(['required', 'notAllWhiteSpace'])}
                    render={props => (
                      <RdnaTextInput
                        type={InputTypes.TEXT}
                        inputProps={{ maxLength: MAX_STRING }}
                        label={'First Name'}
                        {...getMuiProps(props)}
                      />
                    )}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout>
                  <RdnaFormController
                    name="lastName"
                    rules={getRules(['required', 'notAllWhiteSpace'])}
                    render={props => (
                      <RdnaTextInput
                        type={InputTypes.TEXT}
                        inputProps={{ maxLength: MAX_STRING }}
                        label={'Last Name'}
                        {...getMuiProps(props)}
                      />
                    )}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout>
                  <RdnaFormController
                    name="email"
                    rules={getRules(['required', 'notAllWhiteSpace', 'email'])}
                    render={props => (
                      <RdnaTextInput type={InputTypes.EMAIL} label={'Email Address'} {...getMuiProps(props)} />
                    )}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout>
                  <RdnaFormController
                    name="salesforceId"
                    rules={{
                      ...getRules(['required']),
                      validate: (value: string) => {
                        if ((value.length > 0 && !value.trim().length) || value.length < 15 || value.length > 18) {
                          return 'Must contain 15 or 18 characters.'
                        }
                      }
                    }}
                    render={props => (
                      <RdnaTextInput type={InputTypes.TEXT} label={'Salesforce UserID'} {...getMuiProps(props)} />
                    )}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout>
                  <RdnaFormController
                    name="location"
                    rules={getRules(locationRequired ? ['required'] : [])}
                    render={props => (
                      <RdnaAutocomplete2
                        options={locations?.map(l => ({ label: l.name, value: l.id })) || []}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        {...makeAutoCompleteProps(getMuiProps(props))}
                        textInputProps={{
                          required: locationRequired,
                          label: 'Location',
                          placeholder: 'Select',
                          ...makeTextInputProps(getMuiProps(props))
                        }}
                      />
                    )}
                  />
                </RdnaFormLayout>
              </Column>
              <Licenses>
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.intelligentDialer"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.INTELLIGENT_DIALER] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Dialer License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
                {isFlagged(flags.v299_REV_2816) && (
                  <RdnaFormLayout xs={6}>
                    <RdnaFormController
                      name="licenses.revenueAI"
                      rules={{
                        ...getRules(['booleanRequired']),
                        validate: (value: boolean) => {
                          if (remainingLicenses[License.REVENUE_AI] <= 0 && value) {
                            return ERROR_LICENSE
                          }
                        }
                      }}
                      render={props => <RdnaCheckbox label="Revenue AI License" {...getMuiProps(props)} />}
                    />
                  </RdnaFormLayout>
                )}
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.conversationAI"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.CONVERSATION_AI] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Conversation AI License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.calendar"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.CALENDAR] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Calendar Booking License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.moments"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.MOMENTS] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Moments™ License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.admin"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.ADMIN] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Admin License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
                <RdnaFormLayout xs={6}>
                  <RdnaFormController
                    name="licenses.guidedSelling"
                    rules={{
                      ...getRules(['booleanRequired']),
                      validate: (value: boolean) => {
                        if (remainingLicenses[License.GUIDED_SELLING] <= 0 && value) {
                          return ERROR_LICENSE
                        }
                      }
                    }}
                    render={props => <RdnaCheckbox label="Guided Selling License" {...getMuiProps(props)} />}
                  />
                </RdnaFormLayout>
              </Licenses>
              <Actions>
                <RdnaButton type="button" text="Cancel" color="neutral" variant="text" onClick={() => onSave(false)} />
                <RdnaButton type="submit" text="Save" onClick={handleSubmit(onSaveUser)} disabled={isSaving} />
              </Actions>
            </RdnaFormLayout>
          )}
        </RdnaForm>
      </RdnaSection>
    </Drawer>
  )
}

const Column = styled.div`
  width: 60%;
`

const Licenses = styled.div`
  display: contents;
  width: 100%;
  .item {
    margin-bottom: ${props => 2.5 * props.theme.spacing}px !important;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: right;
  margin-bottom: ${props => 10 * props.theme.spacing}px;
  width: 100%;
`

const Drawer = styled(RdnaDrawer)`
  > div {
    width: 50%;
  }
`
